import React, { useState, useEffect, useCallback, useRef } from 'react';
import { Link } from 'react-router-dom';
import { Card, CardContent, CardHeader, CardTitle } from './ui/card';
import { Input } from './ui/input';
import { Label } from './ui/label';
import { Badge } from './ui/badge';
import { Button } from './ui/button';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from './ui/table';
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from './ui/select';
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from './ui/alert-dialog';
import { Loader2, Check, Users, Plus, Trash2, Mail, ExternalLink } from 'lucide-react';
import { eventsAPI, contactsAPI } from '../lib/api';
import { toast } from 'sonner';

const EditableContactsTab = ({ eventId, contacts: initialContacts, onContactsChange }) => {
  const [contacts, setContacts] = useState(initialContacts || []);
  const [allContacts, setAllContacts] = useState([]);
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [deleteConfirm, setDeleteConfirm] = useState({ open: false, index: null, contact: null });

  // Track pending changes and backup timer
  const pendingChanges = useRef(null);
  const backupTimeoutRef = useRef(null);
  const isInitialMount = useRef(true);

  // Only sync with parent on initial mount
  useEffect(() => {
    if (isInitialMount.current) {
      setContacts(initialContacts || []);
      isInitialMount.current = false;
    }
  }, [initialContacts]);

  useEffect(() => {
    loadAllContacts();
  }, []);

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      if (backupTimeoutRef.current) {
        clearTimeout(backupTimeoutRef.current);
      }
    };
  }, []);

  const loadAllContacts = async () => {
    try {
      const res = await contactsAPI.list({ limit: 1000 });
      // Handle paginated response format
      setAllContacts(res.data.data || res.data);
    } catch (error) {
      console.error('Failed to load contacts:', error);
    }
  };

  // Perform the actual save
  const doSave = useCallback(async () => {
    if (pendingChanges.current === null) return;
    
    setSaving(true);
    const contactsToSave = pendingChanges.current;
    pendingChanges.current = null;
    
    try {
      const contactData = contactsToSave.map(c => ({
        contact_id: c.contact_id,
        event_role: c.event_role || ''
      }));
      await eventsAPI.update(eventId, { event_contacts: contactData });
      if (onContactsChange) {
        onContactsChange(contactsToSave);
      }
      setSaved(true);
      setTimeout(() => setSaved(false), 2000);
    } catch (error) {
      console.error('Failed to save contacts:', error);
      toast.error('Failed to save contacts');
      pendingChanges.current = contactsToSave;
    } finally {
      setSaving(false);
    }
  }, [eventId, onContactsChange]);

  // Queue a change
  const queueChange = useCallback((newContacts) => {
    pendingChanges.current = newContacts;
    
    if (backupTimeoutRef.current) {
      clearTimeout(backupTimeoutRef.current);
    }
    
    backupTimeoutRef.current = setTimeout(() => {
      doSave();
    }, 1000);
  }, [doSave]);

  // Save immediately (for blur events)
  const saveNow = useCallback(() => {
    if (backupTimeoutRef.current) {
      clearTimeout(backupTimeoutRef.current);
    }
    doSave();
  }, [doSave]);

  const addContact = () => {
    const newContact = { contact_id: '', contact_name: '', event_role: '', email: '', phone: '', organization_name: '' };
    setContacts([...contacts, newContact]);
  };

  const updateContactSelection = (index, contactId) => {
    const selectedContact = allContacts.find(c => c.id === contactId);
    const newContacts = [...contacts];
    if (selectedContact) {
      newContacts[index] = {
        contact_id: selectedContact.id,
        contact_name: selectedContact.contact_name,
        email: selectedContact.email,
        phone: selectedContact.phone,
        mobile: selectedContact.mobile,
        organization_name: selectedContact.organization_name,
        title: selectedContact.title,
        event_role: newContacts[index].event_role || ''
      };
    } else {
      newContacts[index] = { ...newContacts[index], contact_id: '' };
    }
    setContacts(newContacts);
    if (contactId) {
      queueChange(newContacts);
      saveNow(); // Selection changes should save immediately
    }
  };

  const updateContactRole = (index, role) => {
    const newContacts = [...contacts];
    newContacts[index] = { ...newContacts[index], event_role: role };
    setContacts(newContacts);
    queueChange(newContacts);
  };

  const confirmDeleteContact = (index) => {
    setDeleteConfirm({ open: true, index, contact: contacts[index] });
  };

  const removeContact = () => {
    if (deleteConfirm.index === null) return;
    const newContacts = contacts.filter((_, i) => i !== deleteConfirm.index);
    setContacts(newContacts);
    queueChange(newContacts);
    setDeleteConfirm({ open: false, index: null, contact: null });
    toast.success('Contact removed');
    setTimeout(saveNow, 0);
  };

  // Filter out already selected contacts
  const getAvailableContacts = (currentIndex) => {
    const selectedIds = contacts.map((c, i) => i !== currentIndex ? c.contact_id : null).filter(Boolean);
    return allContacts.filter(c => !selectedIds.includes(c.id));
  };

  return (
    <Card className="border-border/50 bg-card/50">
      <CardHeader>
        <div className="flex items-center justify-between">
          <CardTitle className="text-lg font-semibold flex items-center gap-2">
            <Users className="h-5 w-5 text-cyan-400" />
            Event Contacts
          </CardTitle>
          <div className="flex items-center gap-2 h-6">
            {saving && (
              <Badge variant="outline" className="border-blue-500/30 bg-blue-500/10 text-blue-400">
                <Loader2 className="h-3 w-3 mr-1 animate-spin" />
                Saving...
              </Badge>
            )}
            {saved && !saving && (
              <Badge variant="outline" className="border-emerald-500/30 bg-emerald-500/10 text-emerald-400">
                <Check className="h-3 w-3 mr-1" />
                Saved
              </Badge>
            )}
            <Button type="button" variant="outline" size="sm" onClick={addContact}>
              <Plus className="h-4 w-4 mr-1" /> Add Contact
            </Button>
          </div>
        </div>
      </CardHeader>
      <CardContent>
        {contacts.length > 0 ? (
          <div className="overflow-x-auto">
            <Table>
              <TableHeader>
                <TableRow className="border-border/50">
                  <TableHead className="w-[200px]">Contact</TableHead>
                  <TableHead>Organization</TableHead>
                  <TableHead>Title</TableHead>
                  <TableHead>Email</TableHead>
                  <TableHead>Phone</TableHead>
                  <TableHead>Mobile</TableHead>
                  <TableHead className="w-[150px]">Event Role</TableHead>
                  <TableHead className="w-[50px]"></TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {contacts.map((contact, index) => (
                  <TableRow key={index} className="border-border/50">
                    <TableCell>
                      <div className="flex items-center gap-2">
                        <Select
                          value={contact.contact_id || 'none'}
                          onValueChange={(v) => updateContactSelection(index, v === 'none' ? '' : v)}
                        >
                          <SelectTrigger className="bg-background/50 h-8 w-[180px]">
                            <SelectValue placeholder="Select contact" />
                          </SelectTrigger>
                          <SelectContent>
                            <SelectItem value="none">Select contact</SelectItem>
                            {getAvailableContacts(index).map((c) => (
                              <SelectItem key={c.id} value={c.id}>{c.contact_name}</SelectItem>
                            ))}
                          </SelectContent>
                        </Select>
                        {contact.contact_id && (
                          <Link to={`/contacts/${contact.contact_id}`} className="text-cyan-400 hover:text-cyan-300">
                            <ExternalLink className="h-4 w-4" />
                          </Link>
                        )}
                      </div>
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {contact.organization_name || '-'}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {contact.title || '-'}
                    </TableCell>
                    <TableCell>
                      {contact.email ? (
                        <a href={`mailto:${contact.email}`} className="text-cyan-400 hover:underline flex items-center gap-1">
                          <Mail className="h-3 w-3" />
                          <span className="truncate max-w-[150px]">{contact.email}</span>
                        </a>
                      ) : '-'}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {contact.phone || '-'}
                    </TableCell>
                    <TableCell className="text-muted-foreground">
                      {contact.mobile || '-'}
                    </TableCell>
                    <TableCell>
                      <Input
                        value={contact.event_role || ''}
                        onChange={(e) => updateContactRole(index, e.target.value)}
                        onBlur={saveNow}
                        placeholder="Role"
                        className="bg-background/50 h-8"
                      />
                    </TableCell>
                    <TableCell>
                      <Button variant="ghost" size="icon" onClick={() => confirmDeleteContact(index)} className="hover:bg-destructive/10">
                        <Trash2 className="h-4 w-4 text-destructive" />
                      </Button>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        ) : (
          <p className="text-muted-foreground text-center py-6">
            No contacts linked to this event. Click "Add Contact" to add one.
          </p>
        )}
      </CardContent>

      {/* Delete Confirmation Dialog */}
      <AlertDialog open={deleteConfirm.open} onOpenChange={(open) => !open && setDeleteConfirm({ open: false, index: null, contact: null })}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Remove Contact</AlertDialogTitle>
            <AlertDialogDescription>
              Are you sure you want to remove this contact from the event?
              {deleteConfirm.contact && (
                <span className="block mt-2 font-medium text-foreground">
                  {deleteConfirm.contact.contact_name || 'Unnamed contact'}
                  {deleteConfirm.contact.event_role && ` (${deleteConfirm.contact.event_role})`}
                </span>
              )}
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancel</AlertDialogCancel>
            <AlertDialogAction 
              onClick={removeContact}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Remove
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </Card>
  );
};

export default EditableContactsTab;
